import type { Dispatch, SetStateAction } from 'react';

import { Spinner } from '@uikit';

import { PasskeysRound } from '@uikit/icons/PassKeys';
import { WalletConnect } from '@uikit/icons/WalletConnect';

import { useLogin } from '@shared/hooks/useLogin';

import { CustomDialog, DialogButtonsWrapper } from '@ui/uikit/components/common/Dialog';
import { Karma2 } from '@ui/uikit/components/icons/mono/Karma2';
import { Sqr } from '@ui/uikit/components/icons/poly';

type Props = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onClose?: () => void;
};

export const AuthWeb3Modal = ({ isOpen, setIsOpen, onClose }: Props) => {
  const {
    login: walletConnectOrPasskeyLogin,
    isLogging: isLoggingInWithWallet,
    isLoggingInWithPasskey,
    isLoggingInWithWalletConnect,
  } = useLogin();

  const login = async (type: 'Passkey' | 'WalletConnect2') => {
    return walletConnectOrPasskeyLogin({
      signMessage: 'Confirm authorization in Magic Store with your wallet',
      walletType: type,
      onLoginCallback: () => {
        if (onClose) {
          onClose();
        }

        setIsOpen(false);
      },
    });
  };

  const onOpenChangeHandler = (opened: boolean) => {
    setIsOpen(opened);

    if (onClose && !opened) {
      onClose();
    }
  };

  return (
    <CustomDialog
      open={isOpen}
      onOpenChange={onOpenChangeHandler}
      withGradient
      description={
        <>
          <div className="flex flex-col items-center gap-4 max-w-[352px] mx-auto">
            <Sqr className="size-12" />
            <p className="text-base-text-secondary text-h3-d">Welcome to Magic Square</p>
            <p className=" text-base-text-secondary text-caption-sm-a">
              Your journey into the world of Web3 starts here!
              <Karma2 className="size-4 ml-1 inline-block align-middle text-base-text-pink" />
            </p>
            <p className="text-base-text-secondary text-caption-sm-a">
              Connect an EVM-compatible wallet to continue, or create a new wallet if you don't have
              one
            </p>
          </div>
          <DialogButtonsWrapper className="xs-msq:mt-8 flex-col sm-msq:!flex-row">
            <button
              disabled={isLoggingInWithWallet}
              onClick={() => login('Passkey')}
              className="msq-btn msq-btn-lg msq-btn-secondary w-full md-msq:w-[116px]"
            >
              {isLoggingInWithPasskey && <Spinner size="sm" color="disabled" />}
              {!isLoggingInWithPasskey && (
                <>
                  <PasskeysRound className="icon-xs mr-2" />
                  <span>Create Wallet</span>
                </>
              )}
            </button>

            <button
              disabled={isLoggingInWithWallet}
              onClick={() => login('WalletConnect2')}
              className="msq-btn msq-btn-lg msq-btn-primary w-full md-msq:w-[116px]"
            >
              {isLoggingInWithWalletConnect && <Spinner size="sm" color="disabled" />}
              {!isLoggingInWithWalletConnect && (
                <>
                  <WalletConnect className="icon-xs mr-2" />
                  <span>Wallet Connect</span>
                </>
              )}
            </button>
          </DialogButtonsWrapper>
        </>
      }
    />
  );
};
